import React, { useState } from 'react'
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Alert,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import { Visibility, VisibilityOff, CheckCircle, Cancel } from '@mui/icons-material'

function PasswordRule({ met, label }) {
  return (
    <ListItem disablePadding dense>
      <ListItemIcon sx={{ minWidth: 28 }}>
        {met ? (
          <CheckCircle fontSize="small" color="success" />
        ) : (
          <Cancel fontSize="small" color="disabled" />
        )}
      </ListItemIcon>
      <ListItemText
        primary={label}
        primaryTypographyProps={{ variant: 'caption', color: met ? 'text.primary' : 'text.disabled' }}
      />
    </ListItem>
  )
}

export default function ResetPassword({ reset_password_token, flash = {} }) {
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)

  const rules = {
    length: password.length >= 12,
    match: password.length > 0 && password === passwordConfirmation,
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setErrors({})

    const newErrors = {}
    if (!password) newErrors.password = 'Passwort ist erforderlich'
    else if (password.length < 12) newErrors.password = 'Passwort muss mindestens 12 Zeichen haben'
    if (password !== passwordConfirmation) newErrors.passwordConfirmation = 'Passwörter stimmen nicht überein'
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setSubmitting(true)

    const form = document.createElement('form')
    form.method = 'POST'
    form.action = '/password'

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    const fields = {
      authenticity_token: csrfToken || '',
      _method: 'patch',
      'user[reset_password_token]': reset_password_token || '',
      'user[password]': password,
      'user[password_confirmation]': passwordConfirmation,
    }

    Object.entries(fields).forEach(([name, value]) => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = name
      input.value = value
      form.appendChild(input)
    })

    document.body.appendChild(form)
    form.submit()
  }

  return (
    <Container maxWidth="xs">
      <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="h5" component="h1" fontWeight={700} gutterBottom>
          Neues Passwort setzen
        </Typography>

        {flash.alert && (
          <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
            {flash.alert}
          </Alert>
        )}

        <Paper elevation={2} sx={{ p: 4, width: '100%' }}>
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              label="Neues Passwort"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              margin="normal"
              autoComplete="new-password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={!!errors.password}
              helperText={errors.password}
              slotProps={{
                htmlInput: { 'data-testid': 'password-input' },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            {password.length > 0 && (
              <List dense sx={{ py: 0, mb: 1 }}>
                <PasswordRule met={rules.length} label="Mindestens 12 Zeichen" />
              </List>
            )}

            <TextField
              label="Passwort bestätigen"
              type={showConfirmation ? 'text' : 'password'}
              fullWidth
              margin="normal"
              autoComplete="new-password"
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              error={!!errors.passwordConfirmation}
              helperText={errors.passwordConfirmation}
              slotProps={{
                htmlInput: { 'data-testid': 'password-confirmation-input' },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmation(!showConfirmation)}
                        edge="end"
                        aria-label={showConfirmation ? 'Verbergen' : 'Anzeigen'}
                      >
                        {showConfirmation ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            {passwordConfirmation.length > 0 && (
              <List dense sx={{ py: 0 }}>
                <PasswordRule met={rules.match} label="Passwörter stimmen überein" />
              </List>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={submitting}
              sx={{ mt: 2 }}
              data-testid="reset-password-button"
            >
              {submitting ? 'Speichern...' : 'Passwort speichern'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  )
}
